/**
 * Product form orchestrator — optimistic price + buy-button update, Section
 * Rendering morph for price / picker / controls on variant change, gallery
 * media sync, quantity max from inventory.
 *
 * Morph note: Liquid uses `selected_or_first_available_variant`, which returns
 * the `?variant=` URL match regardless of availability (Shopify docs). Sold-out
 * deep links therefore morph to the same id the picker already applied.
 * Price morph targets values only so a price-nested OSM is not replaced.
 * `_klarna` is a sibling host; its app block is never a morph target.
 * App blocks (`.shopify-app-block`) are never morph targets, including a
 * native Bundles widget if Shopify injects one into the product form.
 */
(() => {
  const TAG = 'product-form';

  if (customElements.get(TAG)) return;

  const UPDATE_SELECTORS = [
    '[data-product-price-values]',
    '[data-variant-picker]',
    '[data-b2b-quantity-note]',
    '[data-b2b-purchasing]',
    '[data-product-form-buttons]',
  ];

  const hasTierPricing = (variant) =>
    Array.isArray(variant?.quantity_price_breaks) &&
    variant.quantity_price_breaks.length > 0;

  /** Cheapest B2B catalog break the quantity qualifies for, else variant price. */
  const unitPriceForQuantity = (variant, quantity) => {
    let price = Number(variant?.price);

    if (!hasTierPricing(variant)) return price;

    for (const tier of variant.quantity_price_breaks) {
      const minimum = Number(tier?.minimum_quantity);
      const tierPrice = Number(tier?.price);

      if (!Number.isFinite(minimum) || !Number.isFinite(tierPrice)) continue;

      if (quantity >= minimum && tierPrice < price) {
        price = tierPrice;
      }
    }

    return price;
  };

  // Shared so the sticky bar paints the same tier without re-deriving it.
  window.themeUnitPriceForQuantity = unitPriceForQuantity;

  const hasPurchasableQuantity = (variant) =>
    window.themeHasPurchasableQuantity?.(variant) !== false;

  class ProductForm extends HTMLElement {
    #abort = null;

    /** Current variant for sibling controls such as sticky ATC and buy buttons. */
    getCurrentVariant() {
      return this.#currentVariantFromPicker();
    }

    connectedCallback() {
      this.addEventListener('variant:change', this.#onVariantChange);
      this.addEventListener('change', this.#onQuantityChange);

      const variant = this.#currentVariantFromPicker();

      this.#syncQuantityFromVariant(variant);
      this.#syncBuyButton(variant);
      this.#syncOosState(variant);
      this.#syncGallery(variant);

      // A catalog minimum can already qualify for a break on first paint.
      if (hasTierPricing(variant)) {
        this.#updatePrice(variant, this.#currentQuantity());
      }
    }

    disconnectedCallback() {
      this.removeEventListener('variant:change', this.#onVariantChange);
      this.removeEventListener('change', this.#onQuantityChange);
      this.#abort?.abort();
    }

    #onVariantChange = (event) => {
      const variant = event.detail?.variant;

      if (!variant) return;

      this.#syncQuantityFromVariant(variant);
      this.#updatePrice(variant, this.#currentQuantity());
      this.#syncBuyButton(variant);
      this.#syncOosState(variant);
      this.#syncGallery(variant);
      this.#fetchAndMorph(variant);
    };

    #onQuantityChange = (event) => {
      if (!event.target?.matches?.('[data-quantity-input]')) return;

      const variant = this.#currentVariantFromPicker();

      if (!hasTierPricing(variant)) return;

      this.#updatePrice(variant, this.#currentQuantity());
    };

    #currentQuantity() {
      const value = Number(this.querySelector('[data-quantity-input]')?.value);

      return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1;
    }

    #sectionId() {
      return this.dataset.sectionId || '';
    }

    #productUrl() {
      return this.dataset.productUrl || window.location.pathname;
    }

    #currentVariantFromPicker() {
      const picker = this.querySelector('product-variant-picker');
      const pickerVariant = picker?.getCurrentVariant?.();

      if (pickerVariant) return pickerVariant;

      const script = this.querySelector('[data-current-variant-json]');

      if (!script) return null;

      try {
        return JSON.parse(script.textContent || 'null');
      } catch {
        return null;
      }
    }

    #syncQuantityFromVariant(variant) {
      const selector = this.querySelector('quantity-selector');

      if (!selector || typeof selector.setLimits !== 'function') return;

      const limits = window.themeQuantityLimitsForVariant?.(variant);

      if (!limits) return;

      selector.setLimits(limits);
    }

    #isNotifyVariant(variant) {
      if (!variant) return false;

      const tracked = Boolean(variant.inventory_management);
      const continueSelling = variant.inventory_policy === 'continue';

      return (
        tracked && !continueSelling && variant.available === false
      );
    }

    #syncOosState(variant) {
      if (!variant) return;

      const unbuyable =
        this.#isNotifyVariant(variant) || !hasPurchasableQuantity(variant);

      this.classList.toggle('is-oos', unbuyable);
    }

    /**
     * Optimistic ATC enable/label so sold-out → in-stock (and reverse) does not
     * wait on Section Rendering morph.
     */
    #syncBuyButton(variant) {
      if (!variant) return;

      const root = this.querySelector('.buy-buttons');
      const button =
        root?.querySelector('.buy-buttons__submit') ||
        root?.querySelector('[type="submit"]') ||
        root?.querySelector('[data-back-in-stock-trigger]');

      if (!(button instanceof HTMLButtonElement) || !root) return;

      const available =
        variant.available !== false && hasPurchasableQuantity(variant);
      const notify = this.#isNotifyVariant(variant);
      const labelEl = button.querySelector('.button__label');
      const addLabel = root.dataset.addLabel || '';
      const soldOutLabel = root.dataset.soldOutLabel || '';
      const notifyLabel = root.dataset.notifyLabel || soldOutLabel;

      root.classList.toggle('buy-buttons--notify', notify);
      button.classList.toggle('buy-buttons__notify', notify);
      button.disabled = !available && !notify;
      button.classList.toggle('button--disabled', !available && !notify);
      button.setAttribute('aria-disabled', available || notify ? 'false' : 'true');
      button.type = notify ? 'button' : 'submit';

      if (notify) {
        button.setAttribute('data-back-in-stock-trigger', '');
        button.setAttribute('aria-haspopup', 'dialog');
      } else {
        button.removeAttribute('data-back-in-stock-trigger');
        button.removeAttribute('aria-haspopup');
      }

      if (labelEl) {
        if (notify && notifyLabel) {
          labelEl.textContent = notifyLabel;
        } else if (!available && soldOutLabel) {
          labelEl.textContent = soldOutLabel;
        } else if (available && addLabel) {
          labelEl.textContent = addLabel;
        }
      }
    }

    #syncGallery(variant) {
      const mediaId = variant?.featured_media_id;

      if (!mediaId) return;

      const section =
        this.closest('[data-quick-add-product], .product') ||
        document.getElementById(`shopify-section-${this.#sectionId()}`) ||
        document;

      const gallery = section.querySelector?.(
        'quick-add-gallery, product-media-gallery'
      );

      if (gallery && typeof gallery.selectByMediaId === 'function') {
        gallery.selectByMediaId(mediaId);
      }
    }

    #moneyFormatter(priceRoot) {
      if (window.Shopify?.formatMoney) {
        return (cents) => window.Shopify.formatMoney(cents);
      }

      const format = priceRoot?.dataset?.moneyFormat;

      if (format) {
        return (cents) => {
          const value = (Number(cents) / 100).toFixed(2);
          const withSeparators = value.replace(
            /\d(?=(\d{3})+\.)/g,
            '$&,'
          );

          return format
            .replace(/\{\{\s*amount\s*\}\}/, withSeparators)
            .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(cents) / 100)))
            .replace(
              /\{\{\s*amount_with_comma_separator\s*\}\}/,
              value.replace('.', ',')
            )
            .replace(
              /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/,
              String(Math.round(Number(cents) / 100))
            );
        };
      }

      const sample =
        priceRoot.querySelector('[data-current-price-value]')?.textContent?.trim() ||
        '';
      const currencyMatch = sample.match(/[^\d\s.,-]+/);
      const currency = currencyMatch ? currencyMatch[0] : '';
      const currencyFirst = Boolean(currency && sample.startsWith(currency));

      return (cents) => {
        const value = (Number(cents) / 100).toFixed(2);

        if (!currency) return value;

        return currencyFirst ? `${currency}${value}` : `${value}${currency}`;
      };
    }

    #updatePrice(variant, quantity = 1) {
      const priceRoot = this.querySelector('[data-product-price]');

      if (!priceRoot || !variant) return;

      const price = unitPriceForQuantity(variant, quantity);

      priceRoot.dataset.variantId = String(variant.id);
      priceRoot.dataset.purchaseAmount = String(price);

      const formatMoney = this.#moneyFormatter(priceRoot);
      const currentValue = priceRoot.querySelector('[data-current-price-value]');
      const compareRoot = priceRoot.querySelector('[data-compare-price]');
      const compareValue = priceRoot.querySelector('[data-compare-price-value]');

      if (currentValue) {
        currentValue.textContent = formatMoney(price);
      }

      const showCompare =
        variant.compare_at_price != null && variant.compare_at_price > price;

      if (compareRoot && compareValue) {
        compareRoot.hidden = !showCompare;

        if (showCompare) {
          compareValue.textContent = formatMoney(variant.compare_at_price);
        }
      }

      // Klarna OSM (when installed) can re-read purchase amount from the host.
      priceRoot.dispatchEvent(
        new CustomEvent('product-price:update', {
          bubbles: true,
          detail: {
            variantId: variant.id,
            purchaseAmount: price,
          },
        })
      );

      this.#syncKlarnaPurchaseAmount(price);
    }

    #syncKlarnaPurchaseAmount(amount) {
      const cents = String(amount);

      for (const placement of this.querySelectorAll('klarna-placement')) {
        placement.setAttribute('data-purchase-amount', cents);
        placement.dataset.purchaseAmount = cents;
      }

      // Klarna's app and library refreshes are global. A form that renders no
      // placement of its own — the quick add drawer — would otherwise rewrite
      // the host page's amount with its product's price.
      if (this.dataset.paymentMessaging === 'false') return;

      // Shopify OSM app: pass the new amount so the app can refresh placements.
      if (typeof window.KOSMApp?.updatePurchaseAmount === 'function') {
        window.KOSMApp.updatePurchaseAmount(amount);
      }

      // Manual / library OSM: refresh after data-purchase-amount changes.
      window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
      window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });

      if (typeof window.Klarna?.OnsiteMessaging?.refresh === 'function') {
        window.Klarna.OnsiteMessaging.refresh();
      }
    }

    async #fetchAndMorph(variant) {
      const sectionId = this.#sectionId();

      if (!sectionId || !variant?.id) return;

      this.#abort?.abort();
      this.#abort = new AbortController();

      const url = new URL(this.#productUrl(), window.location.origin);

      url.searchParams.set('variant', String(variant.id));
      url.searchParams.set('section_id', sectionId);

      try {
        const response = await fetch(url.toString(), {
          signal: this.#abort.signal,
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        this.#morphFromDocument(doc);

        const nextVariant = this.#currentVariantFromPicker() || variant;

        this.#syncQuantityFromVariant(nextVariant);
        this.#syncBuyButton(nextVariant);
        this.#syncOosState(nextVariant);
        this.#syncGallery(nextVariant);

        const restoreScroll = () => {
          if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
            window.scrollTo(scrollX, scrollY);
          }
        };

        restoreScroll();
        window.requestAnimationFrame(restoreScroll);

        // The morph brings back the catalog base price; re-apply the tier.
        if (hasTierPricing(nextVariant)) {
          this.#updatePrice(nextVariant, this.#currentQuantity());
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    #morphFromDocument(doc) {
      for (const selector of UPDATE_SELECTORS) {
        const next = doc.querySelector(selector);
        const current = this.querySelector(selector);

        if (!next || !current) continue;

        if (current.closest('.shopify-app-block')) continue;

        current.replaceWith(next.cloneNode(true));
      }
    }
  }

  customElements.define(TAG, ProductForm);
})();
